// Iter54 — Email verification landing page at /verify?token=...
//
// Reads the `token` query param, calls POST /api/auth/verify-signup, stores
// the returned access token + bootstraps user state, and routes the user
// into the dashboard.
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const mono = (size, color = "#f0f2f8", weight = 400, ls) => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

export default function VerifySignup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [state, setState] = useState({ status: "verifying", message: "Activating your freemium tenant…", data: null });
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!token) {
      setState({ status: "error", message: "Missing verification token in the URL." });
      return;
    }
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/verify-signup`, { token });
        if (data?.token) {
          localStorage.setItem("sitroom_token", data.token);
          axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
        }
        setState({ status: "ok", message: "Welcome to SITROOM!", data });
        setTimeout(() => {
          // Hard-redirect so AuthContext re-reads localStorage on cold boot.
          window.location.assign("/");
        }, 1200);
      } catch (err) {
        const detail = err?.response?.data?.detail || err.message;
        setState({ status: "error", message: typeof detail === "string" ? detail : JSON.stringify(detail) });
      }
    })();
  }, [token]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #08090d 0%, #111420 100%)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
      data-testid="verify-page"
    >
      <div
        style={{
          width: "100%", maxWidth: 440,
          background: "var(--bg-surface, #0d0f18)",
          border: "1px solid rgba(255,255,255,0.10)",
          borderRadius: 8, padding: 32, textAlign: "center",
        }}
      >
        <div className="mb-3" style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 18, letterSpacing: "0.4px", color: "#9ec3ec" }}>
          SITROOM
        </div>
        {state.status === "verifying" && (
          <>
            <div className="mb-3" style={mono(28, "#9ec3ec", 800)}>⟳</div>
            <p style={mono(12, "#c5cad8")} data-testid="verify-status-verifying">{state.message}</p>
          </>
        )}
        {state.status === "ok" && (
          <>
            <div className="mb-3" style={mono(36, "#4ed084", 800)}>✓</div>
            <p style={mono(12, "#9aebc2")} data-testid="verify-status-ok">{state.message}</p>
            <p className="mt-2" style={mono(10, "#9aa1b3")}>
              Tenant <strong>{state.data?.tenant?.name}</strong> created · redirecting…
            </p>
          </>
        )}
        {state.status === "error" && (
          <>
            <div className="mb-3" style={mono(36, "#E24B4A", 800)}>✕</div>
            <p style={mono(12, "#ff9a99")} data-testid="verify-status-error">{state.message}</p>
            <div className="mt-5">
              <Link to="/signup" style={mono(11, "#9ec3ec", 700, "0.4px")}>← Back to signup</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
